import useAuth from '../../auth/UseAuth';
import { GetTransitionEnum, TransitionEnum } from '../../data/ReferenceData';
import { useEffect, useState } from 'react';
import { find, orderBy } from 'lodash';
import { TransitionType } from '../../data/SampleTrackingData';

// Global cache to store all transition enums
let allTransitionEnums: ReadonlyArray<TransitionEnum> = [];

const useTransitionEnums = (): ReadonlyArray<TransitionEnum> => {
  const { accessToken } = useAuth();
  const [transitionEnums, setTransitionEnums] = useState<ReadonlyArray<TransitionEnum>>([]);

  useEffect(() => {
    if (allTransitionEnums.length > 0) {
      setTransitionEnums(orderBy(allTransitionEnums, i => i.name));
      return;
    }

    async function getData() {
      const data = await GetTransitionEnum(accessToken);
      allTransitionEnums = data;
      setTransitionEnums(orderBy(data, i => i.name));
    }

    getData();
  }, [accessToken]);

  return transitionEnums;
};

export const useTransitionEnum = (transitionType: TransitionType): TransitionEnum | undefined => {
  const { accessToken } = useAuth();
  const [transitionEnum, setTransitionEnum] = useState<TransitionEnum | undefined>();

  useEffect(() => {
    if (allTransitionEnums.length > 0) {
      const foundTransitionEnum = find(allTransitionEnums, i => i.name === transitionType);
      if (foundTransitionEnum) {
        setTransitionEnum(foundTransitionEnum);
        return;
      }
    }

    let isCancelled = false;

    GetTransitionEnum(accessToken).then(data => {
      if (isCancelled) {
        return;
      }

      allTransitionEnums = data;
      setTransitionEnum(find(data, i => i.name === transitionType));
    });

    return () => {
      isCancelled = true;
    };
  }, [accessToken, transitionType]);

  return transitionEnum;
};

export const useTransitionEnumById = (transitionEnumId: string | undefined): TransitionEnum | undefined => {
  const { accessToken } = useAuth();
  const [transitionEnum, setTransitionEnum] = useState<TransitionEnum | undefined>();

  useEffect(() => {
    if (!transitionEnumId) {
      setTransitionEnum(undefined);
      return;
    }

    if (allTransitionEnums.length > 0) {
      const foundTransitionEnum = find(allTransitionEnums, i => i.transitionEnumId === transitionEnumId);
      if (foundTransitionEnum) {
        setTransitionEnum(foundTransitionEnum);
        return;
      }
    }

    let isCancelled = false;

    GetTransitionEnum(accessToken).then(data => {
      if (isCancelled) {
        return;
      }

      allTransitionEnums = data;
      setTransitionEnum(find(data, i => i.transitionEnumId === transitionEnumId));
    });

    return () => {
      isCancelled = true;
    };
  }, [accessToken, transitionEnumId]);

  return transitionEnum;
};

export default useTransitionEnums;
